import * as THREE from 'three'
import {
  createColor,
  createVector3,
  type GSplatColorInput,
  type GSplatVector3Input,
  writeColorArray,
  writeVector3Array,
} from '@/plugins/gsplat/effects/effectUtils'
import {
  GSplatShaderEffect,
  type GSplatShaderEffectOptions,
} from '@/plugins/gsplat/effects/GSplatShaderEffect'

const shaderGLSL = /* glsl */ `
uniform float uTime;
uniform vec3 uCenter;
uniform float uBlockCount;
uniform float uBlockSize;
uniform float uDelay;
uniform float uDuration;
uniform float uDotSize;
uniform vec3 uMoveTint;
uniform float uMoveTintIntensity;
uniform vec3 uLandTint;
uniform float uLandDuration;
uniform float uEndRadius;

float g_blockDist;
float g_tStart;
float g_tEnd;

void initShared(vec3 center) {
  vec3 offset = center - uCenter;
  ivec3 blockIdx = ivec3(floor(offset / uBlockSize + vec3(uBlockCount * 0.5)));
  vec3 blockCenter = (vec3(blockIdx) - vec3(uBlockCount * 0.5) + vec3(0.5)) * uBlockSize + uCenter;
  g_blockDist = length(blockCenter - uCenter);
  g_tStart = g_blockDist * uDelay;
  g_tEnd = g_tStart + uDuration;
}

void modifySplatCenter(inout vec3 center) {
  vec3 originalCenter = center;
  initShared(center);

  float timeSinceLanding = uTime - g_tEnd;
  if (timeSinceLanding >= 0.3) return;
  if (g_blockDist > uEndRadius) return;

  if (uTime < g_tStart) {
    center = uCenter;
    return;
  }

  if (uTime < g_tEnd) {
    float progress = (uTime - g_tStart) / uDuration;
    center = mix(uCenter, originalCenter, progress);
  }
}

void modifySplatCovariance(vec3 originalCenter, vec3 modifiedCenter, inout vec3 covA, inout vec3 covB) {
  float timeSinceLanding = uTime - g_tEnd;
  if (timeSinceLanding >= 0.3) return;

  if (g_blockDist > uEndRadius || uTime < g_tStart) {
    gsplatMakeRound(covA, covB, 0.0);
    return;
  }

  float origSize = gsplatExtractSize(covA, covB);
  if (timeSinceLanding < 0.3) {
    if (timeSinceLanding < 0.0) {
      float targetSize = min(uDotSize, origSize);
      gsplatMakeRound(covA, covB, targetSize);
    } else {
      float t = timeSinceLanding * 3.333333;
      float size = mix(uDotSize, origSize, t);
      vec3 roundA = covA;
      vec3 roundB = covB;
      gsplatMakeRound(roundA, roundB, size);
      covA = mix(roundA, covA, t);
      covB = mix(roundB, covB, t);
    }
  }
}

void modifySplatColor(vec3 center, inout vec4 color) {
  float timeSinceLanding = uTime - g_tEnd;
  if (timeSinceLanding >= uLandDuration) return;
  if (g_blockDist > uEndRadius) return;
  if (uTime < g_tStart) return;

  if (timeSinceLanding < 0.0) {
    color.rgb = mix(color.rgb, uMoveTint, uMoveTintIntensity);
  } else if (timeSinceLanding < uLandDuration) {
    float fadeOut = 1.0 - (timeSinceLanding / uLandDuration);
    color.rgb += uLandTint * fadeOut;
  }
}
`

const shaderWGSL = /* wgsl */ `
var<private> g_blockDist: f32;
var<private> g_tStart: f32;
var<private> g_tEnd: f32;

fn initShared(center: vec3f) {
  let offset = center - uniform.uCenter;
  let blockIndex = floor(offset / uniform.uBlockSize +
    vec3f(uniform.uBlockCount * 0.5));
  let blockCenter = (blockIndex - vec3f(uniform.uBlockCount * 0.5) +
    vec3f(0.5)) * uniform.uBlockSize + uniform.uCenter;
  g_blockDist = length(blockCenter - uniform.uCenter);
  g_tStart = g_blockDist * uniform.uDelay;
  g_tEnd = g_tStart + uniform.uDuration;
}

fn modifySplatCenter(center: ptr<function, vec3f>) {
  let originalCenter = *center;
  initShared(*center);

  let timeSinceLanding = uniform.uTime - g_tEnd;
  if (timeSinceLanding >= 0.3 || g_blockDist > uniform.uEndRadius) {
    return;
  }

  if (uniform.uTime < g_tStart) {
    *center = uniform.uCenter;
    return;
  }

  if (uniform.uTime < g_tEnd) {
    let progress = (uniform.uTime - g_tStart) / uniform.uDuration;
    *center = mix(uniform.uCenter, originalCenter, progress);
  }
}

fn modifySplatRotationScale(
  originalCenter: vec3f,
  modifiedCenter: vec3f,
  rotation: ptr<function, vec4f>,
  scale: ptr<function, vec3f>
) {
  let timeSinceLanding = uniform.uTime - g_tEnd;
  if (timeSinceLanding >= 0.3) { return; }

  if (g_blockDist > uniform.uEndRadius || uniform.uTime < g_tStart) {
    *scale = vec3f(0.0);
    return;
  }

  let originalSize = length(*scale);
  if (timeSinceLanding < 0.0) {
    let targetSize = min(uniform.uDotSize, originalSize);
    *scale = vec3f(targetSize);
  } else {
    let t = timeSinceLanding * 3.333333;
    let size = mix(uniform.uDotSize, originalSize, t);
    *scale = mix(vec3f(size), *scale, t);
  }
}

fn modifySplatColor(center: vec3f, color: ptr<function, vec4f>) {
  let timeSinceLanding = uniform.uTime - g_tEnd;
  if (timeSinceLanding >= uniform.uLandDuration ||
      g_blockDist > uniform.uEndRadius ||
      uniform.uTime < g_tStart) {
    return;
  }

  if (timeSinceLanding < 0.0) {
    (*color) = vec4f(
      mix((*color).rgb, uniform.uMoveTint, uniform.uMoveTintIntensity),
      (*color).a
    );
  } else {
    let fadeOut = 1.0 - (timeSinceLanding / uniform.uLandDuration);
    (*color) = vec4f((*color).rgb + uniform.uLandTint * fadeOut, (*color).a);
  }
}
`

const DEFAULT_MIN_PIXEL_SIZE = 0.5

export interface GSplatRevealGridEruptionOptions extends GSplatShaderEffectOptions {
  center?: GSplatVector3Input
  blockCount?: number
  blockSize?: number
  delay?: number
  duration?: number
  dotSize?: number
  moveTint?: GSplatColorInput
  moveTintIntensity?: number
  landTint?: GSplatColorInput
  landDuration?: number
  endRadius?: number
  minPixelSize?: number
}

export class GSplatRevealGridEruption extends GSplatShaderEffect {
  center: THREE.Vector3
  blockCount: number
  blockSize: number
  delay: number
  duration: number
  dotSize: number
  moveTint: THREE.Color
  moveTintIntensity: number
  landTint: THREE.Color
  landDuration: number
  endRadius: number
  minPixelSize: number

  private readonly _centerArray = [0, 0, 0]
  private readonly _moveTintArray = [0, 0, 0]
  private readonly _landTintArray = [0, 0, 0]

  constructor(options: GSplatRevealGridEruptionOptions = {}) {
    super(options)
    this.center = createVector3(options.center, new THREE.Vector3(0, 0, 0))
    this.blockCount = options.blockCount ?? 10
    this.blockSize = options.blockSize ?? 2
    this.delay = options.delay ?? 0.2
    this.duration = options.duration ?? 1
    this.dotSize = options.dotSize ?? 0.01
    this.moveTint = createColor(options.moveTint, new THREE.Color(1, 0, 1))
    this.moveTintIntensity = options.moveTintIntensity ?? 0.2
    this.landTint = createColor(options.landTint, new THREE.Color(2, 2, 0))
    this.landDuration = options.landDuration ?? 0.6
    this.endRadius = options.endRadius ?? 25
    this.minPixelSize = options.minPixelSize ?? DEFAULT_MIN_PIXEL_SIZE
    this.uniforms.minPixelSize = { value: this.minPixelSize }
  }

  getShaderGLSL(): string {
    return shaderGLSL
  }

  getShaderWGSL(): string {
    return shaderWGSL
  }

  isEffectComplete(): boolean {
    const maxTStart = this.endRadius * this.delay
    const maxTEnd = maxTStart + this.duration
    return this.effectTime >= maxTEnd + Math.max(0.3, this.landDuration)
  }

  protected override updateEffect(effectTime: number): void {
    if (this.isEffectComplete()) {
      this.enabled = false
      return
    }

    this.setUniform('uTime', effectTime)
    this.setUniform('uCenter', writeVector3Array(this.center, this._centerArray))
    this.setUniform('uBlockCount', this.blockCount)
    this.setUniform('uBlockSize', this.blockSize)
    this.setUniform('uDelay', this.delay)
    this.setUniform('uDuration', this.duration)
    this.setUniform('uDotSize', this.dotSize)
    this.setUniform('uMoveTint', writeColorArray(this.moveTint, this._moveTintArray))
    this.setUniform('uMoveTintIntensity', this.moveTintIntensity)
    this.setUniform('uLandTint', writeColorArray(this.landTint, this._landTintArray))
    this.setUniform('uLandDuration', this.landDuration)
    this.setUniform('uEndRadius', this.endRadius)
    this.setUniform('minPixelSize', this.minPixelSize)
  }
}
